
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCertificateReportingStats, findUnreportedCertificates } from '@/utils/reportUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CertificateDiscrepancyAlert } from './CertificateDiscrepancyAlert';
import { CertificateDiscrepancyList } from './CertificateDiscrepancyList';
import { CertificateTrackingStats } from './CertificateTrackingStats';
import { CertificateAuditTrail } from './CertificateAuditTrail';
import { CertificateDetailView } from './CertificateDetailView';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface CertificateDiscrepancy {
  id: string;
  certificate_number: string;
  state: string;
  course_type: string;
  is_used: boolean;
  assigned_at?: string | null;
  created_at?: string | null;  // Add the created_at field
  reporting_status?: string | null;
  verification_status?: string | null;
  discrepancyType: 'unreported' | 'unverified' | 'conflicting';
  discrepancyDetails?: string;
}

export function CertificateTrackingDashboard() {
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState<string>('FL');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState<number>(
    Math.floor((new Date().getMonth() / 3)) + 1
  );
  const [activeTab, setActiveTab] = useState('overview');
  const [discrepancies, setDiscrepancies] = useState<CertificateDiscrepancy[]>([]);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [selectedCertificateNumber, setSelectedCertificateNumber] = useState<string>('');
  
  // Query for certificate reporting stats
  const statsQuery = useQuery({
    queryKey: ['certificateReportingStats', selectedState, selectedYear, selectedQuarter],
    queryFn: async () => {
      const { data, error } = await getCertificateReportingStats(
        selectedState, 
        selectedYear, 
        selectedQuarter
      );
      
      if (error) throw error;
      return data;
    }
  });

  // Query for certificate inventory
  const inventoryQuery = useQuery({
    queryKey: ['certificateInventory', selectedState, selectedYear, selectedQuarter],
    queryFn: async () => {
      // Calculate quarter date range
      const startMonth = (selectedQuarter - 1) * 3;
      const startDate = new Date(selectedYear, startMonth, 1);
      const endDate = new Date(selectedYear, startMonth + 3, 0);
      
      // Format for PostgreSQL
      const formattedStartDate = startDate.toISOString();
      const formattedEndDate = endDate.toISOString();
      
      const { data, error } = await supabase
        .from('certificate_number_inventory')
        .select('*')
        .eq('state', selectedState)
        .gte('created_at', formattedStartDate)
        .lte('created_at', formattedEndDate);
      
      if (error) throw error;
      return data || [];
    }
  });
  
  // Detect discrepancies
  useEffect(() => {
    if (statsQuery.data && inventoryQuery.data) {
      const newDiscrepancies: CertificateDiscrepancy[] = [];
      
      // Find unreported certificates
      inventoryQuery.data.forEach((cert: any) => {
        if (cert.is_used && cert.reporting_status !== 'reported') {
          newDiscrepancies.push({
            ...cert,
            discrepancyType: 'unreported',
            discrepancyDetails: 'Certificate has been used but not reported'
          });
        }
        
        // Find unverified certificates
        if (cert.is_used && cert.verification_status !== 'verified') {
          newDiscrepancies.push({
            ...cert,
            discrepancyType: 'unverified',
            discrepancyDetails: 'Certificate has been used but not verified'
          });
        }
        
        // Find conflicting status
        if (
          (cert.reporting_status === 'reported' && cert.verification_status !== 'verified') ||
          (cert.verification_status === 'verified' && cert.reporting_status !== 'reported')
        ) {
          newDiscrepancies.push({
            ...cert,
            discrepancyType: 'conflicting',
            discrepancyDetails: 'Certificate has conflicting reporting and verification status'
          });
        }
      });
      
      // Remove duplicates
      const uniqueDiscrepancies = Array.from(
        new Map(newDiscrepancies.map(item => [item.id, item])).values()
      );
      
      setDiscrepancies(uniqueDiscrepancies);
    }
  }, [statsQuery.data, inventoryQuery.data]);
  
  // Generate year options (current year +/- 2 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => 
    new Date().getFullYear() - 2 + i
  );

  const handleResolveClick = () => {
    setActiveTab('discrepancies');
  };
  
  const handleResolveDiscrepancy = (discrepancy: CertificateDiscrepancy) => {
    // Open the certificate detail view
    setSelectedCertificateNumber(discrepancy.certificate_number);
    setIsDetailViewOpen(true);
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Certificate Tracking Dashboard</CardTitle>
        <CardDescription>
          Monitor certificate reporting compliance and detect discrepancies
        </CardDescription>
        <div className="flex flex-wrap gap-2 mt-4">
          <Select
            value={selectedState}
            onValueChange={setSelectedState}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FL">Florida</SelectItem>
              <SelectItem value="CA">California</SelectItem>
              <SelectItem value="TX">Texas</SelectItem>
              <SelectItem value="NY">New York</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={selectedYear.toString()}
            onValueChange={(value) => setSelectedYear(parseInt(value))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select
            value={selectedQuarter.toString()}
            onValueChange={(value) => setSelectedQuarter(parseInt(value))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Q1</SelectItem>
              <SelectItem value="2">Q2</SelectItem>
              <SelectItem value="3">Q3</SelectItem>
              <SelectItem value="4">Q4</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {statsQuery.isLoading || inventoryQuery.isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : statsQuery.isError || inventoryQuery.isError ? (
          <div className="flex justify-center p-8 text-red-500">
            Error loading certificate data
          </div>
        ) : (
          <div className="space-y-6">
            {/* Discrepancy Alert */}
            <CertificateDiscrepancyAlert 
              discrepancyCount={discrepancies.length} 
              onResolveClick={handleResolveClick}
            />
            
            <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-3 w-[600px]">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="discrepancies">
                  Discrepancies
                  {discrepancies.length > 0 && (
                    <span className="ml-2 bg-red-100 text-red-800 rounded-full px-2 py-0.5 text-xs">
                      {discrepancies.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="audit">Audit Trail</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-6 mt-6">
                <CertificateTrackingStats
                  totalCertificates={inventoryQuery.data?.length || 0}
                  reported={statsQuery.data?.totalReported || 0}
                  verified={(inventoryQuery.data || []).filter((cert: any) => 
                    cert.verification_status === 'verified'
                  ).length}
                  unreported={statsQuery.data?.unreported || 0}
                  unverified={(inventoryQuery.data || []).filter((cert: any) => 
                    cert.is_used && cert.verification_status !== 'verified'
                  ).length}
                />
                
                {/* Add a condensed discrepancy list if there are any */}
                {discrepancies.length > 0 && (
                  <div className="mt-6">
                    <CertificateDiscrepancyList
                      discrepancies={discrepancies.slice(0, 5)}
                      onResolve={handleResolveDiscrepancy}
                    />
                    
                    {discrepancies.length > 5 && (
                      <div className="text-center mt-2">
                        <Button 
                          variant="link" 
                          onClick={() => setActiveTab('discrepancies')}
                        >
                          View all {discrepancies.length} discrepancies
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="discrepancies" className="space-y-6 mt-6">
                <CertificateDiscrepancyList
                  discrepancies={discrepancies}
                  onResolve={handleResolveDiscrepancy}
                />
              </TabsContent>
              
              <TabsContent value="audit" className="space-y-6 mt-6">
                <CertificateAuditTrail limit={50} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </CardContent>

      {/* Certificate Detail View Modal */}
      <CertificateDetailView
        certificateNumber={selectedCertificateNumber}
        open={isDetailViewOpen}
        onOpenChange={setIsDetailViewOpen}
      />
    </Card>
  );
}
